import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { api, logout } from '../api';
import { useI18n } from '../i18n/context';
import './SuperAdminLayout.css';

const SIDEBAR_STATE_KEY = 'sa_sidebar_collapsed';
const NAV_SECTIONS = [
  {
    titleKey: 'navSections.general',
    items: [
      { to: '/', end: true, key: 'nav.dashboard', icon: 'dashboard' },
      { to: '/audit', end: false, key: 'nav.audit', icon: 'audit' },
      { to: '/notification-diagnostics', end: false, key: 'nav.notifDiagnostics', icon: 'bell' },
      { to: '/system-status', end: false, key: 'nav.systemStatus', icon: 'shield' },
      { to: '/backups', end: false, key: 'nav.backups', icon: 'database' }
    ]
  },
  {
    titleKey: 'navSections.management',
    items: [
      { to: '/organizations', end: false, key: 'nav.organizations', icon: 'orgs' },
      { to: '/users', end: false, key: 'nav.users', icon: 'users' },
      { to: '/time-entries', end: false, key: 'nav.timeEntries', icon: 'time' }
    ]
  },
  {
    titleKey: 'navSections.security',
    items: [
      { to: '/security', end: false, key: 'nav.security', icon: 'shield' },
      { to: '/change-password', end: false, key: 'nav.changePassword', icon: 'lock' }
    ]
  }
];

export default function SuperAdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, locale, setLocale } = useI18n();
  const [user, setUser] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const savedLocale = window.localStorage.getItem('sa_locale');
    if (savedLocale && savedLocale !== locale) {
      setLocale(savedLocale);
    }
    const saved = window.localStorage.getItem(SIDEBAR_STATE_KEY);
    if (saved === '1') setSidebarCollapsed(true);
  }, []);

  useEffect(() => {
    api('/superadmin/auth/me')
      .then((data) => {
        setUser(data.user);
        if (data.preferences?.locale && data.preferences.locale !== locale) {
          setLocale(data.preferences.locale);
        }
      })
      .catch(() => logout());
  }, []);

  async function handleLocaleChange(newLocale) {
    setLocale(newLocale);
    window.localStorage.setItem('sa_locale', newLocale);
    try {
      await api('/superadmin/auth/preferences', { method: 'PATCH', body: JSON.stringify({ locale: newLocale }) });
    } catch (_) {}
  }

  function currentPageTitle(pathname) {
    if (pathname.startsWith('/users')) return t('nav.users');
    if (pathname.startsWith('/organizations')) return t('nav.organizations');
    if (pathname.startsWith('/time-entries')) return t('nav.timeEntries');
    if (pathname.startsWith('/audit')) return t('nav.audit');
    if (pathname.startsWith('/notification-diagnostics')) return t('nav.notifDiagnostics');
    if (pathname.startsWith('/backups')) return t('nav.backups');
    if (pathname.startsWith('/security')) return t('nav.security');
    if (pathname.startsWith('/change-password')) return t('nav.changePassword');
    return t('nav.dashboard');
  }

  function currentPageSubtitle(pathname) {
    if (pathname.startsWith('/users')) return t('users.subtitle');
    if (pathname.startsWith('/organizations')) return t('org.subtitle');
    if (pathname.startsWith('/time-entries')) return t('timeEntries.subtitle');
    if (pathname.startsWith('/audit')) return t('audit.subtitle');
    if (pathname.startsWith('/notification-diagnostics')) return t('notifDiagnostics.subtitle');
    if (pathname.startsWith('/backups')) return t('backups.subtitle');
    if (pathname.startsWith('/security')) return t('security.overviewTitle');
    if (pathname.startsWith('/change-password')) return t('password.changeDescription');
    return t('dashboard.subtitle');
  }

  function toggleSidebar() {
    setSidebarCollapsed((c) => {
      const next = !c;
      window.localStorage.setItem(SIDEBAR_STATE_KEY, next ? '1' : '0');
      return next;
    });
  }

  function NavIcon({ name }) {
    if (name === 'dashboard') {
      return (
        <svg viewBox="0 0 24 24" className="sa-nav-icon" aria-hidden="true">
          <path d="M4 4h7v7H4zM13 4h7v5h-7zM13 11h7v9h-7zM4 13h7v7H4z" />
        </svg>
      );
    }
    if (name === 'orgs') {
      return (
        <svg viewBox="0 0 24 24" className="sa-nav-icon" aria-hidden="true">
          <path d="M3 10h8V4H3v6zm10 10h8V4h-8v16zM3 20h8v-8H3v8z" />
        </svg>
      );
    }
    if (name === 'users') {
      return (
        <svg viewBox="0 0 24 24" className="sa-nav-icon" aria-hidden="true">
          <path d="M7.5 12a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm9 3a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7zM3 20a4.5 4.5 0 0 1 9 0v1H3v-1zm11-1a4 4 0 0 1 8 0v1h-8v-1z" />
        </svg>
      );
    }
    if (name === 'time') {
      return (
        <svg viewBox="0 0 24 24" className="sa-nav-icon" aria-hidden="true">
          <path d="M12 3a9 9 0 1 1 0 18 9 9 0 0 1 0-18zm1 4h-2v6l4 2 1-1.7-3-1.3V7z" />
        </svg>
      );
    }
    if (name === 'audit') {
      return (
        <svg viewBox="0 0 24 24" className="sa-nav-icon" aria-hidden="true">
          <path d="M4 3h10l6 6v12H4zM13 3v6h6M7 13h10v2H7zm0 4h10v2H7z" />
        </svg>
      );
    }
    if (name === 'bell') {
      return (
        <svg viewBox="0 0 24 24" className="sa-nav-icon" aria-hidden="true">
          <path d="M12 2a2 2 0 0 0-2 2v.3C7.1 5.1 5 7.8 5 11v4l-2 3v1h18v-1l-2-3v-4c0-3.2-2.1-5.9-5-6.7V4a2 2 0 0 0-2-2zm0 20a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22z" />
        </svg>
      );
    }
    if (name === 'shield') {
      return (
        <svg viewBox="0 0 24 24" className="sa-nav-icon" aria-hidden="true">
          <path d="M12 3l8 3v6c0 5-3.4 8.7-8 10-4.6-1.3-8-5-8-10V6l8-3z" />
        </svg>
      );
    }
    if (name === 'database') {
      return (
        <svg viewBox="0 0 24 24" className="sa-nav-icon" aria-hidden="true">
          <path d="M12 3c-4.97 0-9 1.79-9 4s4.03 4 9 4 9-1.79 9-4-4.03-4-9-4zm0 6c-3.87 0-7-1.12-7-2s3.13-2 7-2 7 1.12 7 2-3.13 2-7 2zm0 3c-4.97 0-9-1.79-9-4v3c0 2.21 4.03 4 9 4s9-1.79 9-4v-3c0 2.21-4.03 4-9 4zm0 2c-3.87 0-7-1.12-7-2s3.13-2 7-2 7 1.12 7 2-3.13 2-7 2zm0 1c-4.97 0-9-1.79-9-4v3c0 2.21 4.03 4 9 4s9-1.79 9-4v-3c0 2.21-4.03 4-9 4zm0 2c-3.87 0-7-1.12-7-2s3.13-2 7-2 7 1.12 7 2-3.13 2-7 2z" />
        </svg>
      );
    }
    return (
      <svg viewBox="0 0 24 24" className="sa-nav-icon" aria-hidden="true">
        <path d="M6 3h12v8H6zM4 13h16v8H4z" />
      </svg>
    );
  }

  return (
    <div className="sa-layout">
      <div className={`sa-sidebar-backdrop ${mobileOpen ? 'sa-sidebar-backdrop--open' : ''}`} onClick={() => setMobileOpen(false)} />
      <aside
        className={`sa-sidebar ${sidebarCollapsed ? 'sa-sidebar--collapsed' : ''} ${mobileOpen ? 'sa-sidebar--open' : ''}`}
        style={{ width: sidebarCollapsed ? 64 : 240 }}
      >
        <div className="sa-sidebar-header">
          {!sidebarCollapsed && (
            <div className="sa-sidebar-brand">
              <img src="/logo.png" alt="" className="sa-sidebar-logo-img" />
              <div className="sa-sidebar-logo-text">
                <span className="sa-sidebar-logo">ChatIN Clock</span>
                <span className="sa-sidebar-subtitle">{t('nav.backofficeSubtitle')}</span>
              </div>
            </div>
          )}
          {sidebarCollapsed && (
            <div className="sa-sidebar-brand-icon-only">
              <img src="/logo.png" alt="" className="sa-sidebar-logo-img" />
            </div>
          )}
          <button
            type="button"
            className="sa-sidebar-toggle"
            onClick={toggleSidebar}
            aria-label={sidebarCollapsed ? 'Expandir' : 'Colapsar'}
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>
        </div>
        <nav className="sa-sidebar-nav">
          {NAV_SECTIONS.map((section) => (
            <div key={section.titleKey} className="sa-sidebar-section">
              <div className="sa-sidebar-section-title">{t(section.titleKey)}</div>
              {section.items.map(({ to, end, key, icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => 'sa-sidebar-link' + (isActive ? ' sa-sidebar-link--active' : '')}
                >
                  <NavIcon name={icon} />
                  <span className="sa-sidebar-link-text">{t(key)}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </aside>
      <div className="sa-main" style={{ marginLeft: sidebarCollapsed ? 64 : 240 }}>
        <header className="sa-header">
          <button
            type="button"
            className="sa-mobile-menu"
            onClick={() => setMobileOpen(true)}
            aria-label={t('nav.openMenu')}
          >
            <span className="sa-mobile-menu-bar" />
            <span className="sa-mobile-menu-bar" />
            <span className="sa-mobile-menu-bar" />
          </button>
          <div className="sa-header-actions">
            <div className="sa-locale-selector">
              <button
                type="button"
                className={'sa-locale-btn' + (locale === 'en' ? ' sa-locale-btn--active' : '')}
                onClick={() => handleLocaleChange('en')}
              >
                EN
              </button>
              <button
                type="button"
                className={'sa-locale-btn' + (locale === 'es' ? ' sa-locale-btn--active' : '')}
                onClick={() => handleLocaleChange('es')}
              >
                ES
              </button>
            </div>
            <span className="sa-header-user">{user?.name || user?.email || '—'}</span>
            <button type="button" className="sa-btn-secondary sa-header-logout" onClick={logout}>
              {t('common.logout')}
            </button>
          </div>
        </header>
        <main className="sa-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
